import { useMemo } from 'react';
import { CircleAlert } from 'lucide-react';
import type { TaskEvent, TaskMember } from '@shared/types/tasks';
import { disputeOpen, disputeUnfinished } from '@shared/factory-dispute';
import type { DisputePick } from './FactoryDisputeCard';
import type { SuggestionPick } from './FactorySuggestionsCard';
import { DecisionRoundCard, DisputeRoundCard } from './TaskDecisions';
import { SuggestionRoundCard } from './TaskSuggestions';
import { decisionRounds, taskDisputeRounds } from './task-decisions';
import { roundDecided, suggestionRounds } from './task-suggestions';
import { subtaskProposals } from './task-subtask-proposals';
import { SubtaskProposalCard } from './ProposedSubtasks';
import { useSuggestionTaskRefs } from './use-suggestion-task-refs';
import type { UpstreamTagging } from './SendUpstreamButton';

/**
 * T-58: a mirrored task's source, for the cards here: its name, whether the task waits on it, and
 * (an enabled source, someone who can comment) sending a round's chosen items there.
 */
export interface RoundUpstream {
  sourceLabel: string;
  waiting?: boolean;
  onSend?: (eventId: string, numbers: number[], mentionId: string | null) => Promise<boolean>;
  /** SF-T-101: who a send can tag. */
  tagging?: UpstreamTagging;
}

/** The id of a task's To answer block. */
const toAnswerAnchorId = (taskId: string) => `task-to-answer-${taskId}`;

/**
 * T-72: what's still waiting on people from the AI builder, at the top of the task (under its
 * properties): its open questions (T-22) and the suggestions not yet decided (T-14), newest first, in a
 * highlighted block. SF-T-74: a run's disputed review to settle (or whose settling didn't finish) is
 * here too, before them. SF-T-178: so are the sub-tasks it proposed and nobody has added or rejected yet,
 * after the suggestions. Once answered, decided or settled, a round moves to the task's Decisions,
 * Suggestions or Proposed sub-tasks, folded. Nothing shows when nothing's waiting.
 */
export function TaskToAnswer({
  taskId,
  events,
  members,
  onAnswer,
  onApply,
  onDismiss,
  onSettle,
  onAddSubtasks,
  proposals = true,
  onNavigate,
  upstream,
  suggestionChats,
}: {
  taskId: string;
  /** The task's events, oldest first. */
  events: TaskEvent[];
  members: TaskMember[];
  /** Posts the answers as a chat message on the task, naming the round (T-58: its message's id); leave out where nobody can answer (a preview). */
  onAnswer?: (message: string, eventId: string) => void;
  /** Applies one run's suggestion picks (decideTaskSuggestions); resolves true when applied. Leave out in a preview. */
  onApply?: (eventId: string, decisions: Array<SuggestionPick & { number: number }>) => Promise<boolean>;
  /** SF-T-178: No thanks on a run's suggestions still open (declineTaskSuggestions); resolves true when done. Leave out where nobody can. */
  onDismiss?: (eventId: string) => Promise<boolean>;
  /** SF-T-178: adds a proposal's ticked sub-tasks, or rejects them all with none (addProposedSubtasks). Leave out where nobody can. */
  onAddSubtasks?: (eventId: string, numbers: number[]) => Promise<boolean>;
  /** Whether undecided sub-task proposals wait here (false on a sub-task, which can't have sub-tasks: ProposedSubtasks lists them). */
  proposals?: boolean;
  /** SF-T-74: settles a disputed review's findings (settleTaskDispute); resolves true when it worked. Leave out where nobody can. */
  onSettle?: (eventId: string, picks: DisputePick[]) => Promise<boolean>;
  /** Called when a link to a new task is followed (closes a pop-up). */
  onNavigate?: () => void;
  /** T-58: on a mirrored task, its source: for the questions and for the suggestions. */
  upstream?: { questions?: RoundUpstream; suggestions?: RoundUpstream };
  /** SF-T-304: each suggestion's live chat; `canAsk` when the person can comment. Leave out in a preview. */
  suggestionChats?: { canAsk: boolean };
}) {
  const questions = useMemo(() => decisionRounds(events).filter((r) => !r.answered), [events]);
  const suggestions = useMemo(() => suggestionRounds(events).filter((r) => !roundDecided(r)), [events]);
  const disputes = useMemo(() => taskDisputeRounds(events).filter((d) => disputeOpen(d) || disputeUnfinished(d)), [events]);
  const subtasks = useMemo(() => (proposals ? subtaskProposals(events).filter((p) => !p.decided) : []), [events, proposals]);
  const taskRefs = useSuggestionTaskRefs(suggestions);
  if (!questions.length && !suggestions.length && !disputes.length && !subtasks.length) return null;
  const answers = questions.length > 0 || disputes.length > 0;
  const decisions = suggestions.length > 0 || subtasks.length > 0;

  return (
    <section
      id={toAnswerAnchorId(taskId)}
      className="scroll-mt-4 space-y-3 rounded-lg border border-warning-border bg-warning-soft/40 p-3"
      aria-label="To answer"
    >
      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-warning-strong">
        <CircleAlert className="size-4" aria-hidden />
        {answers && decisions ? 'Waiting for your answers and decisions' : answers ? 'Waiting for your answers' : 'Waiting for your decisions'}
      </h3>
      {disputes.map((round) => (
        <DisputeRoundCard key={round.message.id} round={round} onSettle={onSettle} />
      ))}
      {questions.map((round) => (
        <DecisionRoundCard
          key={round.event.id}
          round={round}
          members={members}
          onAnswer={onAnswer ? (message) => onAnswer(message, round.event.id) : undefined}
          upstream={upstream?.questions}
          chat={suggestionChats ? { taskId, canAsk: suggestionChats.canAsk } : undefined}
        />
      ))}
      {suggestions.map((round) => (
        <SuggestionRoundCard
          key={round.event.id}
          round={round}
          taskRefs={taskRefs}
          onApply={onApply}
          onDismiss={onDismiss}
          onNavigate={onNavigate}
          upstream={upstream?.suggestions}
          chat={suggestionChats ? { taskId, canAsk: suggestionChats.canAsk } : undefined}
        />
      ))}
      {subtasks.map((proposal) => (
        <SubtaskProposalCard key={proposal.event.id} proposal={proposal} onAdd={onAddSubtasks} />
      ))}
    </section>
  );
}
